<script setup>
import { Mail, ArrowUpRight } from 'lucide-vue-next';
import PdEyebrow from '../PdEyebrow.vue';

defineProps({
  email: { type: String, default: '121@paydeck.in' },
  note: { type: String, default: 'For customer support, payment questions, fraud reports and partnership enquiries.' },
  /** Guidance tiles: { title, body } */
  guides: {
    type: Array,
    default: () => [
      { title: 'Help us find your case', body: 'If your question is about a payment, include your transaction ID, the date, amount and a short explanation. For an account issue, write from the email address linked to your Paydeck account.' },
      { title: 'Keep sensitive details private', body: 'Never send your card PIN, CVV, OTP or password by email. If you suspect unauthorised activity, contact your bank immediately and read our fraud reporting guidance.' },
    ],
  },
});
</script>

<template>
  <div class="pd-contact">
    <a :href="`mailto:${email}`" class="pd-contact__main">
      <PdEyebrow tone="ink">The direct line to our team</PdEyebrow>
      <span class="pd-contact__icon"><Mail :size="28" aria-hidden="true" /></span>
      <span class="pd-contact__lab">Email Paydeck</span>
      <span class="pd-contact__email">{{ email }}</span>
      <span class="pd-contact__note">{{ note }}</span>
      <ArrowUpRight class="pd-contact__arr" :size="28" aria-hidden="true" />
    </a>
    <div v-for="(g, i) in guides" :key="g.title" class="pd-contact__guide">
      <p class="pd-contact__gn pd-num">{{ String(i + 1).padStart(2, '0') }} /</p>
      <h3 class="pd-contact__gt">{{ g.title }}</h3>
      <p class="pd-contact__gb">{{ g.body }}</p>
    </div>
  </div>
</template>

<style scoped>
.pd-contact { display: grid; grid-template-columns: 1fr 1fr; gap: var(--pd-gap); }
.pd-contact__main { position: relative; grid-column: 1 / -1; display: flex; flex-direction: column; gap: 8px; padding: clamp(24px, 4vw, 40px); border-radius: var(--pd-r-lg); background: var(--pd-orange); color: var(--pd-on-orange); text-decoration: none; }
.pd-contact__icon { display: grid; place-items: center; width: 60px; height: 60px; margin: 16px 0 8px; border-radius: 18px; background: var(--pd-ink); color: var(--pd-orange); }
.pd-contact__lab { font-weight: 800; }
.pd-contact__email { font-size: clamp(1.75rem, 5vw, 3.5rem); font-weight: 800; letter-spacing: -0.055em; line-height: 1; overflow-wrap: anywhere; }
.pd-contact__note { max-width: 460px; font-weight: 600; }
.pd-contact__arr { position: absolute; top: clamp(24px, 4vw, 40px); right: clamp(24px, 4vw, 40px); transition: transform var(--pd-fast) var(--pd-ease); }
.pd-contact__main:hover .pd-contact__arr { transform: translate(3px, -3px); }
.pd-contact__guide { padding: 24px; border-radius: var(--pd-r-lg); background: var(--pd-cream); border: 1px solid var(--pd-line); }
.pd-contact__gn { font-weight: 800; color: var(--pd-orange-text); }
.pd-contact__gt { margin: 8px 0; font-size: 1.25rem; font-weight: 800; letter-spacing: -0.03em; }
.pd-contact__gb { color: var(--pd-muted); font-size: 0.9375rem; }
@media (max-width: 720px) { .pd-contact { grid-template-columns: minmax(0, 1fr); } }
</style>
